import { create } from 'zustand';
import { authEvents } from '../api/client';
import { identifyClarityUser, clearClarityUser, trackAuthEvent } from '../observability/clarity';
import { identifySentryUser } from '../observability/sentry';

export type Role =
  | 'SuperAdmin' | 'SchoolAdmin' | 'Teacher' | 'Accountant' | 'Parent' | 'Student'
  | 'Librarian' | 'TransportManager' | 'HRManager' | 'FrontDesk' | 'ExamCoordinator' | 'DeviceAdmin';

export type Locale = 'ne' | 'hi' | 'en';

export interface Session {
  userId: string;
  roles: Role[];
  activeRole: Role;
  children: { id: string; name: string }[];
  activeChildId: string | null;
  localeOverride?: Locale;
  schoolDefaultLocale: Locale;
  onboardingDismissed?: boolean;
}

interface AuthState {
  session: Session | null;
  expired: boolean;
  setSession: (s: Session) => void;
  switchRole: (role: Role) => void;
  switchChild: (childId: string) => void;
  dismissOnboarding: () => void;
  expire: () => void;
  sessionExpired: () => void;
}

function buildUserCtx(s: Session, onboardingState?: 'not-started' | 'in-progress' | 'completed' | 'dismissed') {
  return {
    userId: s.userId,
    role: s.activeRole,
    authState: 'authenticated' as const,
    onboardingState: onboardingState ?? (s.onboardingDismissed ? 'completed' : 'not-started'),
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  expired: false,
  setSession: (s) => {
    set({ session: s, expired: false });
    const ctx = buildUserCtx(s);
    identifyClarityUser(ctx);
    identifySentryUser(ctx);
    trackAuthEvent('auth-login-succeeded');
  },
  switchRole: (role) => {
    const s = get().session;
    if (!s) throw new Error('no session');
    if (!s.roles.includes(role)) throw new Error(`role not held: ${role}`);
    const updated = { ...s, activeRole: role };
    set({ session: updated });
    const ctx = buildUserCtx(updated);
    identifyClarityUser(ctx);
    identifySentryUser(ctx);
    trackAuthEvent('role-switch');
  },
  switchChild: (childId) => {
    const s = get().session;
    if (!s) throw new Error('no session');
    set({ session: { ...s, activeChildId: childId } });
  },
  dismissOnboarding: () => {
    const s = get().session;
    if (!s) return;
    set({ session: { ...s, onboardingDismissed: true } });
    const ctx = buildUserCtx(s, 'dismissed');
    identifyClarityUser(ctx);
    identifySentryUser(ctx);
  },
  expire: () => {
    set({ session: null, expired: false });
    clearClarityUser();
    identifySentryUser(null);
    trackAuthEvent('auth-logout-clicked');
  },
  sessionExpired: () => {
    set({ session: null, expired: true });
    clearClarityUser();
    identifySentryUser(null);
    trackAuthEvent('auth-session-expired');
  },
}));

// Tie the Axios refresh-failure event to the store.
authEvents.addEventListener('auth:expired', () => useAuthStore.getState().sessionExpired());
